import { DESIGN_MODE_CUSTOM_CSS_SCRIPT_SELECTOR } from "@/constants";
import { REVIEWS_WIDGET_NAMES } from "@/constants/widget-names";
import { urlsBuilderContextToken } from "@/context_tokens";
import { growaveConfigurationToken, modalManagerToken } from "@/tokens";
import { isShadowRoot } from "@/utils/isShadowRoot/isShadowRoot";
import { reviewsIconCollectionManagerContextToken } from "@apps/reviews/context_tokens";
import ReviewsStorefrontValuesService from "@apps/reviews/services/ReviewsStorefrontValuesService";
import VotesStore from "@apps/reviews/stores/VotesStore";
import {
    reviewsAuthApiClientToken,
    reviewsIconCollectionManagerToken,
} from "@apps/reviews/tokens";
import ProductReviewsQuery from "@apps/reviews/widgets/reviews_page_widget/queries/ProductReviewsQuery";
import ProductReviewsService from "@apps/reviews/widgets/reviews_page_widget/services/ProductReviewsService";
import ProductReviewsStore from "@apps/reviews/widgets/reviews_page_widget/stores/ProductReviewsStore";
import CustomizationManager from "@lib/CustomizationManager/CustomizationManager";
import { CommonModule } from "@modules/common_module/CommonModule";
import GrowaveStorefrontValuesModule from "@modules/growave_storefront_values/GrowaveStorefrontValuesModule";
import GrowaveStorefrontValuesService from "@modules/growave_storefront_values/services/GrowaveStorefrontValuesService";
import { I18nModule } from "@modules/i18n/I18nModule";
import { container } from "tsyringe";

import { ModalManager } from "@components/Modal/ModalManager";
import { LoggerInterface } from "@interfaces/LoggerInterface";

import ReviewsGalleryManager from "../reviews_gallery_widget/ReviewsGalleryManager";
import SiteReviewsQuery from "./queries/SiteReviewsQuery";
import ReviewsPageWidgetComponent from "./ReviewsPageWidget.svelte";
import { ReviewsPageWidgetConfigInterface } from "./ReviewsPageWidgetConfig";
import SiteReviewsService from "./services/SiteReviewsService";
import SiteReviewsStore from "./stores/SiteReviewsStore";

class ReviewsPageWidget {
    private readonly productReviewsStore: ProductReviewsStore;
    private readonly productReviewsService: ProductReviewsService;
    private readonly productReviewsQuery: ProductReviewsQuery;

    private readonly siteReviewsService: SiteReviewsService;
    private readonly siteReviewsStore: SiteReviewsStore;
    private readonly siteReviewsQuery: SiteReviewsQuery;

    private readonly reviewsStorefrontValuesService: ReviewsStorefrontValuesService;
    private readonly growaveStorefrontValuesService: GrowaveStorefrontValuesService;

    private readonly modalManager: ModalManager;
    private readonly reviewsGalleryManager: ReviewsGalleryManager;

    constructor(
        private readonly config: ReviewsPageWidgetConfigInterface,
        private readonly logger: LoggerInterface
    ) {
        // I18n
        const i18nModule = container.resolve(I18nModule);
        i18nModule.exports.i18n.registerLoader(
            "reviews.ReviewsPageWidget",
            i18nModule.exports.i18nApiLoaderFactory.getLoader(
                "reviews.ReviewsPageWidget"
            )
        );
        i18nModule.exports.i18n.registerLoader(
            "reviews.critical.ReviewsPageWidget",
            i18nModule.exports.i18nCriticalLoaderFactory.getLoader(
                "reviews.critical.ReviewsPageWidget"
            )
        );

        this.logger.debug("ReviewsPageWidget is initialized", config);
        const api = container.resolve(reviewsAuthApiClientToken);

        // Reviews
        const votesStore = container.resolve(VotesStore);
        this.productReviewsStore = new ProductReviewsStore();
        this.productReviewsService = new ProductReviewsService(
            this.productReviewsStore,
            votesStore,
            api,
            this.logger
        );
        this.productReviewsQuery = new ProductReviewsQuery(
            this.productReviewsStore
        );

        this.siteReviewsStore = new SiteReviewsStore();
        this.siteReviewsService = new SiteReviewsService(
            this.siteReviewsStore,
            votesStore,
            api,
            this.logger
        );
        this.siteReviewsQuery = new SiteReviewsQuery(this.siteReviewsStore);

        this.reviewsStorefrontValuesService = container.resolve(
            ReviewsStorefrontValuesService
        );

        this.growaveStorefrontValuesService = container.resolve(
            GrowaveStorefrontValuesModule
        ).exports.growaveStorefrontValuesService;

        this.modalManager = container.resolve(modalManagerToken);
        this.reviewsGalleryManager = container.resolve(ReviewsGalleryManager);
    }

    async init(): Promise<this> {
        const customizationManager = container.resolve(CustomizationManager);
        const growaveConfiguration = container.resolve(
            growaveConfigurationToken
        );

        const element = this.config.target;
        const shadowRoot = isShadowRoot(
            this.config.isWidgetShadowRootEnabled,
            growaveConfiguration.isShadowRootDisabledGlobally
        )
            ? element.shadowRoot || element.attachShadow({ mode: "open" })
            : null;

        const customizationManagerConfig = {
            widgetName: REVIEWS_WIDGET_NAMES.REVIEWS_PAGE_WIDGET,
            placeholder: element,
            shadowRoot,
        };

        customizationManager.injectStylesFromDesignMode({
            element,
            shadowRoot,
            scriptSelector: DESIGN_MODE_CUSTOM_CSS_SCRIPT_SELECTOR,
        });
        customizationManager.injectStylesFromCustomization(
            customizationManagerConfig
        );
        await customizationManager.beforeWidgetInject(
            customizationManagerConfig
        );

        const contextMap = new Map();
        contextMap.set(
            reviewsIconCollectionManagerContextToken,
            container.resolve(reviewsIconCollectionManagerToken)
        );
        contextMap.set(
            urlsBuilderContextToken,
            container.resolve(CommonModule).exports.urlsBuilder
        );

        new ReviewsPageWidgetComponent({
            target: shadowRoot || element,
            props: {
                modalManager: this.modalManager,
                reviewsGalleryManager: this.reviewsGalleryManager,
                reviewsStorefrontValuesService:
                    this.reviewsStorefrontValuesService,
                productReviewsQuery: this.productReviewsQuery,
                productReviewsService: this.productReviewsService,
                siteReviewsQuery: this.siteReviewsQuery,
                siteReviewsService: this.siteReviewsService,
                growaveStorefrontValuesService:
                    this.growaveStorefrontValuesService,
                reviewsPerPage: this.config.reviewsPerPage,
                afterWidgetInject: () => {
                    customizationManager.afterWidgetInject(
                        customizationManagerConfig
                    );
                },
            },
            context: contextMap,
        });

        return this;
    }
}

export default ReviewsPageWidget;
